import React, { useEffect, useState } from "react";
import {
  Briefcase,
  Calendar,
  Clock,
  DollarSign,
  Building2,
  Trash2,
  Eye,
  Zap,
  AlertCircle,
  CheckCircle,
  X,
  User,
  Check,
  XCircle
} from "lucide-react";

import adminService from "../../../../services/adminService";
import { getJobApplications, updateApplicationStatus } from "../../../../services/applicationService";
import CompanyViewFreelancerModal from "../../../../Components/CompanyViewFreelancerModal/CompanyViewFreelancerModal";
import "./Adminjobs.scss";

const STATUS_LABEL = {
  abertas: "Aberta",
  andamento: "Em Andamento",
  finalizadas: "Finalizada",
};

const STATUS_BADGE_CLASS = {
  abertas: "open",
  andamento: "in-progress",
  finalizadas: "completed",
};

const STATUS_ICONS = {
  abertas: Briefcase,
  andamento: Clock,
  finalizadas: CheckCircle,
};

// Valores reais que o ApplicationsController usa no campo `status`
// (strings em minúsculo, ver JobApplication.Status no backend).
const APPLICATION_STATUS_LABEL = {
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Recusado",
  invited: "Convidado",
  completed: "Concluído",
  early_exit: "Saída antecipada",
  no_show: "Não compareceu",
  filter_rejected: "Não atendeu aos requisitos",
};

const APPLICATION_STATUS_BADGE_CLASS = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
  invited: "pending",
  completed: "approved",
  early_exit: "rejected",
  no_show: "rejected",
  filter_rejected: "rejected",
};

function normalizeStatus(status) {
  return String(status ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

// A API de candidaturas pode devolver o freelancer de formas
// diferentes dependendo do endpoint (objeto aninhado ou campos soltos).
// Essas funções cobrem os formatos mais comuns pra não quebrar a tela
// enquanto o formato exato não é confirmado.
function getApplicantName(application) {
  return application.freelancer?.nomeSocial || application.freelancer?.name || "Freelancer";
}

function getApplicantAvatar(application) {
  return application.freelancer?.avatarUrl ?? null;
}

function getApplicationId(application) {
  return application.id;
}

function getAppliedAtLabel(application) {
  const raw = application.createdAt;
  if (!raw) return null;

  return new Date(raw).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function formatCurrency(v) {
  return (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function CandidatesModal({ job, onClose }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [viewingFreelancerId, setViewingFreelancerId] = useState(null);

  const loadApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getJobApplications(job.id);
      setApplications(data ?? []);
    } catch (err) {
      setError("Não foi possível carregar os candidatos dessa vaga.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const handleStatusChange = async (application, newStatus) => {
    const applicationId = getApplicationId(application);
    setUpdatingId(applicationId);

    try {
      await updateApplicationStatus(applicationId, newStatus);
      await loadApplications();
    } catch (err) {
      alert("Erro ao atualizar status do candidato. Tente novamente.");
    } finally {
      setUpdatingId(null);
    }
  };

  const approvedCount = applications.filter(
    (a) => normalizeStatus(a.status) === "approved"
  ).length;

  return (
    <>
    <div className="candidates-modal-overlay" onClick={onClose}>
      <div
        className="candidates-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="candidates-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="candidates-modal__header">
          <div>
            <h3 id="candidates-modal-title">Candidatos — {job.title}</h3>
            <span className="candidates-modal__subtitle">
              {applications.length} candidatura{applications.length !== 1 ? "s" : ""}
              {" · "}
              {approvedCount} aprovado{approvedCount !== 1 ? "s" : ""}
            </span>
          </div>

          <button
            type="button"
            className="candidates-modal__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="candidates-modal__body">
          {loading && (
            <div className="candidates-modal__state">Carregando candidatos...</div>
          )}

          {!loading && error && (
            <div className="candidates-modal__state candidates-modal__state--error">
              {error}
            </div>
          )}

          {!loading && !error && applications.length === 0 && (
            <div className="candidates-modal__state">
              Ninguém se candidatou a essa vaga ainda.
            </div>
          )}

          {!loading && !error && applications.length > 0 && (
            <ul className="candidates-modal__list">
              {applications.map((application) => {
                const applicationId = getApplicationId(application);
                const statusKey = normalizeStatus(application.status);
                const statusLabel = APPLICATION_STATUS_LABEL[statusKey] ?? application.status;
                const badgeClass = APPLICATION_STATUS_BADGE_CLASS[statusKey] ?? "pending";
                const isUpdating = updatingId === applicationId;
                const appliedAtLabel = getAppliedAtLabel(application);
                const avatarUrl = getApplicantAvatar(application);

                return (
                  <li className="candidates-modal__item" key={applicationId}>
                    <div className="candidates-modal__avatar">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" />
                      ) : (
                        <User size={18} />
                      )}
                    </div>

                    <button
                      type="button"
                      className="candidates-modal__info"
                      onClick={() => setViewingFreelancerId(application.freelancer?.id)}
                      disabled={!application.freelancer?.id}
                      aria-label={`Ver perfil de ${getApplicantName(application)}`}
                    >
                      <span className="candidates-modal__name">
                        {getApplicantName(application)}
                      </span>
                      {appliedAtLabel && (
                        <span className="candidates-modal__date">
                          Candidatou-se em {appliedAtLabel}
                        </span>
                      )}
                      <p className="candidates-modal__name">Clique para ver o perfil do freelancer</p>
                    </button>

                    <span
                      className={`candidates-modal__badge candidates-modal__badge--${badgeClass}`}
                    >
                      {statusLabel}
                    </span>

                    {statusKey === "pending" && (
                      <div className="candidates-modal__actions">
                        <button
                          type="button"
                          className="candidates-modal__btn candidates-modal__btn--approve"
                          disabled={isUpdating}
                          onClick={() => handleStatusChange(application, "approved")}
                        >
                          <Check size={14} />
                          Aprovar
                        </button>

                        <button
                          type="button"
                          className="candidates-modal__btn candidates-modal__btn--reject"
                          disabled={isUpdating}
                          onClick={() => handleStatusChange(application, "rejected")}
                        >
                          <XCircle size={14} />
                          Recusar
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
    {viewingFreelancerId && (
      <CompanyViewFreelancerModal
        freelancerId={viewingFreelancerId}
        onClose={() => setViewingFreelancerId(null)}
      />
    )}
    </>
  );
}

export default function AdminJobs({ status, title, onChanged }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidatesJob, setCandidatesJob] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminService.getJobs(status);
      setJobs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const handleDelete = async (job) => {
    if (!window.confirm(`Tem certeza que deseja apagar a vaga "${job.title}"?`)) return;
    try {
      await adminService.deleteJob(job.id);
      await load();
      onChanged?.();
    } catch (error) {
      alert("Erro ao deletar vaga. Tente novamente.");
    }
  };

  const StatusIcon = STATUS_ICONS[status] || Briefcase;
  const statusLabel = STATUS_LABEL[status] || status || "desconhecido";
  const badgeClass = STATUS_BADGE_CLASS[status] || "open";

  return (
    <div className="admin-jobs">
      {/* Header */}
      <div className="admin-jobs__header">
        <div className="admin-jobs__title">
          <StatusIcon size={20} />
          {title}
        </div>
        <span className="admin-jobs__count">
          Total <span>{jobs.length}</span>
        </span>
      </div>

      {/* Loading */}
      {loading && (
        <div className="admin-jobs__loading">
          Carregando vagas...
        </div>
      )}

      {/* Lista de Vagas */}
      {!loading && jobs.length > 0 && (
        <div className="admin-jobs__list">
          {jobs.map((job) => (
            <div className="admin-jobs__item" key={job.id}>
              {/* Ícone */}
              <div className="admin-jobs__item-icon">
                <Briefcase size={20} />
              </div>

              {/* Informações */}
              <div className="admin-jobs__item-info">
                <div className="admin-jobs__item-top">
                  <h4 className="admin-jobs__item-title">{job.title}</h4>
                  <span className={`admin-jobs__item-badge admin-jobs__item-badge--${badgeClass}`}>
                    {statusLabel}
                  </span>
                  {job.isUrgent && (
                    <span className="admin-jobs__item-badge admin-jobs__item-badge--urgent">
                      <Zap size={12} />
                      Urgente
                    </span>
                  )}
                </div>

                <div className="admin-jobs__item-meta">
                  <span>
                    <Building2 size={14} />
                    {job.companyName}
                  </span>
                  <span>•</span>
                  <span>
                    <Calendar size={14} />
                    {formatDate(job.date)}
                  </span>
                  <span>•</span>
                  <span>
                    <Clock size={14} />
                    {job.startTime}
                    {job.endTime ? ` - ${job.endTime}` : ""}
                  </span>
                  <span>•</span>
                  <span className="admin-jobs__item-payment">
                    <DollarSign size={14} />
                    {formatCurrency(job.payment)}
                  </span>
                </div>
              </div>

              {/* Ações */}
              <div className="admin-jobs__item-actions">
                <button
                  className="admin-jobs__btn admin-jobs__btn--view"
                  onClick={() => setCandidatesJob(job)}
                >
                  <Eye size={14} />
                  Ver candidatos
                </button>

                <button
                  className="admin-jobs__btn admin-jobs__btn--delete"
                  onClick={() => handleDelete(job)}
                >
                  <Trash2 size={14} />
                  Apagar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && jobs.length === 0 && (
        <div className="admin-jobs__empty">
          <AlertCircle size={40} />
          <h3>Nenhuma vaga encontrada</h3>
          <p>
            Não há vagas com status "{String(statusLabel || "desconhecido").toLowerCase()}" no momento.
          </p>
        </div>
      )}

      {candidatesJob && (
        <CandidatesModal
          job={candidatesJob}
          onClose={() => setCandidatesJob(null)}
        />
      )}
    </div>
  );
}